import React, { useState } from 'react';
import {
  Truck,
  Package,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Edit,
  Plus,
  ArrowRightLeft,
  MapPin,
  Calendar,
  Building,
  Filter,
  Search,
  Download,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';

interface StockTransferRequest {
  id: string;
  fromPharmacy: string;
  toPharmacy: string;
  medication: string;
  quantity: number;
  status: 'pending' | 'approved' | 'in_transit' | 'received' | 'rejected';
  requestDate: string;
  expectedDate?: string;
  reason: string;
  requestedBy: string;
  approvedBy?: string;
  notes?: string;
}

const StockTransfer: React.FC = () => {
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Mock stock transfer data
  const stockTransfers: StockTransferRequest[] = [
    {
      id: 'transfer-001',
      fromPharmacy: 'MedFast Westfield',
      toPharmacy: 'MedFast Sydney Central',
      medication: 'Warfarin 5mg',
      quantity: 100,
      status: 'pending',
      requestDate: '2024-09-15T09:00:00Z',
      reason: 'Urgent aged care order requirement',
      requestedBy: 'Sarah Johnson',
      notes: 'Patient requires immediate medication'
    },
    {
      id: 'transfer-002',
      fromPharmacy: 'MedFast Bondi Junction',
      toPharmacy: 'MedFast Sydney Central',
      medication: 'Insulin Glargine',
      quantity: 10,
      status: 'approved',
      requestDate: '2024-09-14T15:30:00Z',
      expectedDate: '2024-09-16T10:00:00Z',
      reason: 'Cold chain medication for NDIS patient',
      requestedBy: 'John Smith',
      approvedBy: 'Dr. Michael Brown',
      notes: 'Temperature controlled transport required'
    },
    {
      id: 'transfer-003',
      fromPharmacy: 'MedFast Sydney Central',
      toPharmacy: 'MedFast Parramatta',
      medication: 'Rosuvastatin 20mg',
      quantity: 50,
      status: 'in_transit',
      requestDate: '2024-09-14T11:00:00Z',
      expectedDate: '2024-09-16T14:00:00Z',
      reason: 'Stock balancing between stores',
      requestedBy: 'Emma Wilson',
      approvedBy: 'Dr. Michael Brown',
      notes: 'Regular stock rotation'
    },
    {
      id: 'transfer-004',
      fromPharmacy: 'MedFast Chatswood',
      toPharmacy: 'MedFast Sydney Central',
      medication: 'Metformin 500mg',
      quantity: 200,
      status: 'received',
      requestDate: '2024-09-13T08:00:00Z',
      expectedDate: '2024-09-15T12:00:00Z',
      reason: 'High demand medication',
      requestedBy: 'David Lee',
      approvedBy: 'Dr. Michael Brown',
      notes: 'Successfully received and processed'
    },
    {
      id: 'transfer-005',
      fromPharmacy: 'MedFast Bondi Junction',
      toPharmacy: 'MedFast Westfield',
      medication: 'Atorvastatin 20mg',
      quantity: 75,
      status: 'rejected',
      requestDate: '2024-09-12T14:00:00Z',
      reason: 'Insufficient stock at source',
      requestedBy: 'Lisa Chen',
      notes: 'Stock levels too low for transfer'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-blue-100 text-blue-800';
      case 'in_transit': return 'bg-orange-100 text-orange-800';
      case 'received': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'approved': return <CheckCircle className="h-4 w-4" />;
      case 'in_transit': return <Truck className="h-4 w-4" />;
      case 'received': return <Package className="h-4 w-4" />;
      case 'rejected': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const filteredTransfers = stockTransfers.filter(transfer => {
    const matchesSearch = transfer.medication.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transfer.fromPharmacy.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transfer.toPharmacy.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatus === 'all' || transfer.status === selectedStatus;
    return matchesSearch && matchesStatus;
  });

  const handleApprove = (transferId: string) => {
    console.log(`Approving transfer: ${transferId}`);
    // In a real app, this would update the database
  };

  const handleReject = (transferId: string) => {
    console.log(`Rejecting transfer: ${transferId}`);
    // In a real app, this would update the database
  };

  const handleMarkReceived = (transferId: string) => {
    console.log(`Marking transfer as received: ${transferId}`);
    // In a real app, this would update the database
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Stock Transfers</h1>
          <p className="text-gray-600 mt-2">Manage inter-pharmacy stock movements and transfers</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2">
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>New Transfer</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="group bg-gradient-to-br from-blue-500 to-blue-600 p-8 rounded-3xl shadow-xl text-white hover:shadow-2xl transition-all duration-500 hover:scale-105 hover:-translate-y-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-semibold mb-2 tracking-wide uppercase">Total Transfers</p>
              <p className="text-4xl font-bold mb-2">{stockTransfers.length}</p>
              <p className="text-blue-100 text-xs">All time transfers</p>
            </div>
            <div className="p-4 bg-white/20 rounded-2xl group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
              <Truck className="h-8 w-8" />
            </div>
          </div>
          <div className="mt-4 w-full bg-white/20 rounded-full h-2">
            <div className="bg-white rounded-full h-2 transition-all duration-1000 group-hover:animate-pulse" style={{ width: '75%' }} />
          </div>
        </div>

        <div className="group bg-gradient-to-br from-yellow-500 to-orange-500 p-8 rounded-3xl shadow-xl text-white hover:shadow-2xl transition-all duration-500 hover:scale-105 hover:-translate-y-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm font-semibold mb-2 tracking-wide uppercase">Pending Approval</p>
              <p className="text-4xl font-bold mb-2">
                {stockTransfers.filter(t => t.status === 'pending').length}
              </p>
              <p className="text-yellow-100 text-xs">Awaiting review</p>
            </div>
            <div className="p-4 bg-white/20 rounded-2xl group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
              <Clock className="h-8 w-8" />
            </div>
          </div>
          <div className="mt-4 w-full bg-white/20 rounded-full h-2">
            <div className="bg-white rounded-full h-2 transition-all duration-1000 group-hover:animate-pulse" style={{ width: '60%' }} />
          </div>
        </div>

        <div className="group bg-gradient-to-br from-orange-500 to-red-500 p-8 rounded-3xl shadow-xl text-white hover:shadow-2xl transition-all duration-500 hover:scale-105 hover:-translate-y-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-semibold mb-2 tracking-wide uppercase">In Transit</p>
              <p className="text-4xl font-bold mb-2">
                {stockTransfers.filter(t => t.status === 'in_transit').length}
              </p>
              <p className="text-orange-100 text-xs">Currently moving</p>
            </div>
            <div className="p-4 bg-white/20 rounded-2xl group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
              <Package className="h-8 w-8" />
            </div>
          </div>
          <div className="mt-4 w-full bg-white/20 rounded-full h-2">
            <div className="bg-white rounded-full h-2 transition-all duration-1000 group-hover:animate-pulse" style={{ width: '40%' }} />
          </div>
        </div>

        <div className="group bg-gradient-to-br from-green-500 to-emerald-600 p-8 rounded-3xl shadow-xl text-white hover:shadow-2xl transition-all duration-500 hover:scale-105 hover:-translate-y-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-semibold mb-2 tracking-wide uppercase">Completed</p>
              <p className="text-4xl font-bold mb-2">
                {stockTransfers.filter(t => t.status === 'received').length}
              </p>
              <p className="text-green-100 text-xs">Successfully delivered</p>
            </div>
            <div className="p-4 bg-white/20 rounded-2xl group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
              <CheckCircle className="h-8 w-8" />
            </div>
          </div>
          <div className="mt-4 w-full bg-white/20 rounded-full h-2">
            <div className="bg-white rounded-full h-2 transition-all duration-1000 group-hover:animate-pulse" style={{ width: '90%' }} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/80 backdrop-blur-md p-8 rounded-3xl shadow-xl border border-white/30">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1">
            <div className="relative group">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 group-hover:text-purple-500 transition-colors duration-300" />
              <input
                type="text"
                placeholder="Search transfers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-300 bg-gray-50 focus:bg-white"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-300 bg-gray-50 focus:bg-white font-medium"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="in_transit">In Transit</option>
              <option value="received">Received</option>
              <option value="rejected">Rejected</option>
            </select>
            <button className="px-6 py-4 text-gray-700 bg-gradient-to-r from-gray-100 to-gray-200 rounded-2xl hover:from-gray-200 hover:to-gray-300 flex items-center space-x-2 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl">
              <Filter className="h-5 w-5" />
              <span className="font-medium">Filter</span>
            </button>
          </div>
        </div>
      </div>

      {/* Transfers Table */}
      <div className="bg-white/80 backdrop-blur-md rounded-3xl shadow-xl border border-white/30 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
              <tr>
                <th className="px-8 py-6 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Transfer ID</th>
                <th className="px-8 py-6 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">From → To</th>
                <th className="px-8 py-6 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Medication</th>
                <th className="px-8 py-6 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Quantity</th>
                <th className="px-8 py-6 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Status</th>
                <th className="px-8 py-6 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Request Date</th>
                <th className="px-8 py-6 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredTransfers.map((transfer) => (
                <tr key={transfer.id} className="group hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 transition-all duration-300">
                  <td className="px-8 py-6 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900 group-hover:text-gray-700">{transfer.id}</div>
                    <div className="text-sm text-gray-500 font-medium">by {transfer.requestedBy}</div>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg group-hover:scale-110 transition-transform duration-300">
                        <Building className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-sm font-bold text-gray-900 group-hover:text-gray-700">{transfer.fromPharmacy}</div>
                        <div className="flex items-center text-sm text-gray-500 font-medium">
                          <ArrowRightLeft className="h-4 w-4 mr-2 text-gray-400" />
                          {transfer.toPharmacy}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900 group-hover:text-gray-700">{transfer.medication}</div>
                    <div className="text-sm text-gray-500 font-medium">{transfer.reason}</div>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900 group-hover:text-gray-700">{transfer.quantity}</div>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap">
                    <span className={`inline-flex items-center px-4 py-2 rounded-full text-xs font-bold shadow-sm ${getStatusColor(transfer.status)}`}>
                      {getStatusIcon(transfer.status)}
                      <span className="ml-2">{transfer.status.replace('_', ' ').toUpperCase()}</span>
                    </span>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900 group-hover:text-gray-700">{format(new Date(transfer.requestDate), 'dd/MM/yyyy')}</div>
                    <div className="text-sm text-gray-500 font-medium">{format(new Date(transfer.requestDate), 'HH:mm')}</div>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-3">
                      <button className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-xl transition-all duration-300 hover:scale-110 group/btn">
                        <Eye className="h-5 w-5 group-hover/btn:scale-110 transition-transform duration-300" />
                      </button>
                      {transfer.status === 'pending' && (
                        <>
                          <button 
                            onClick={() => handleApprove(transfer.id)}
                            className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-xl transition-all duration-300 hover:scale-110 group/btn"
                          >
                            <CheckCircle className="h-5 w-5 group-hover/btn:scale-110 transition-transform duration-300" />
                          </button>
                          <button 
                            onClick={() => handleReject(transfer.id)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-xl transition-all duration-300 hover:scale-110 group/btn"
                          >
                            <XCircle className="h-5 w-5 group-hover/btn:scale-110 transition-transform duration-300" />
                          </button>
                        </>
                      )}
                      {transfer.status === 'approved' && (
                        <button 
                          onClick={() => handleMarkReceived(transfer.id)}
                          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-xl transition-all duration-300 hover:scale-110 group/btn"
                        >
                          <Package className="h-5 w-5 group-hover/btn:scale-110 transition-transform duration-300" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Transfer Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Stock Transfer</h3>
            <p className="text-sm text-gray-500 mb-4">
              This feature will be implemented in the next phase.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockTransfer;
